import React, { useEffect } from "react";
import {
  BlockHeroCompact,
  BlockSection,
  BlockText,
  BlockFigure,
  ImageSequence,
  RecipeRow,
  RecipeCell,
  RecipeBoard,
  projectPageShellClass,
  projectPageBottomClass,
  blockSectionBodyWrapClass,
  blockItalicSecondaryNoteClass,
} from "../../designSystem/projectBlocks";

const BASE = "/projects/SILTMiddelkerke/images";

/**
 * SILT Middelkerke — composed with 12-column recipes + site tokens.
 * Every figure is pinned to one of the four canonical aspect ratios
 * (banner 3:1, video 16:9, classic 4:3, square 1:1).
 */
export default function SILTMiddelkerkePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stripA = [
    `${BASE}/D07-b.png`,
    `${BASE}/D05-c.png`,
    `${BASE}/D04.png`,
    `${BASE}/D03-d.png`,
    `${BASE}/D02.png`,
  ];
  const stripB = [
    `${BASE}/D08-b.png`,
    `${BASE}/D08.png`,
    `${BASE}/D05.png`,
    `${BASE}/D03-c.png`,
    `${BASE}/D03-A.png`,
  ];

  const iterationCapsA = ["Detail D07-b", "Detail D05-c", "Detail D04", "Detail D03-d", "Detail D02"];
  const iterationCapsB = ["Detail D08-b", "Detail D08", "Detail D05", "Detail D03-c", "Detail D03-A"];

  const smallOutputs = ["Output1", "Output2", "Output3", "Output4", "Output6", "Output7"];

  return (
    <main className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-500">
      <div className={`${projectPageShellClass} pt-[72px] ${projectPageBottomClass} box-border`}>
        <BlockHeroCompact
          title="SILT Middelkerke"
          description="Rhino–Grasshopper geometries streamed into Cadwork-ready timber and steel assemblies — symmetry reduction, attributed parts, and fabricator-native outputs."
          meta={["Studio ZJA", "Middelkerke", "Grasshopper", "Rhino", "Cadwork"]}
        />

        {/* Hero media — 3:1 banner */}
        <RecipeBoard>
          <RecipeRow>
            <RecipeCell span={12} className="!p-0 !border-r-0">
              <BlockFigure
                src={`${BASE}/Frame_0.png`}
                alt="SILT Middelkerke — exterior architectural render"
                ratio="banner"
                fit="cover"
              />
            </RecipeCell>
          </RecipeRow>
        </RecipeBoard>

        {/* Input driving geometry — text full-width, then 4+4+4 of square diagrams */}
        <BlockSection title="Input driving geometry">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "The geometry was optimised for very tight tolerances and for reducing unique parts by applying symmetry wherever possible. The requirement was a pipeline that outputs Cadwork-readable solids with attributes the Cadwork API can translate into native assemblies — including full sub-assembly hierarchy.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure
                  src={`${BASE}/MasterSrf.png`}
                  alt="Master surface diagram"
                  ratio="square"
                  fit="contain"
                  caption="Master surface"
                />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0">
                <BlockFigure
                  src={`${BASE}/SymmetryPlanes.png`}
                  alt="Symmetry planes diagram"
                  ratio="square"
                  fit="contain"
                  caption="Symmetry planes"
                />
              </RecipeCell>
              <RecipeCell span={4} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/InitialNodes.png`}
                  alt="Initial seed nodes diagram"
                  ratio="square"
                  fit="contain"
                  caption="Initial nodes"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        {/* Provided details — 8+4: big initial-grid figure + two sequence players */}
        <BlockSection title="Provided details">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Initial grid context beside progressive detail strips — major field plus paired iteration sequences.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={8} className="!p-0">
                <BlockFigure
                  src={`${BASE}/InitialGrid.png`}
                  alt="Initial structural grid"
                  ratio="classic"
                  fit="contain"
                  caption="Initial structural grid"
                />
              </RecipeCell>
              <RecipeCell span={4} className="!p-3 !border-r-0 gap-4 flex flex-col">
                <ImageSequence
                  images={stripA}
                  captions={iterationCapsA}
                  label="Iteration strip · A"
                  ratio="classic"
                />
                <ImageSequence
                  images={stripB}
                  captions={iterationCapsB}
                  label="Iteration strip · B"
                  ratio="classic"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        {/* Object attributes — 2 × (6 + 6), all classic 4:3 */}
        <BlockSection title="Object attributes">
          <BlockText
            className={blockSectionBodyWrapClass}
            paragraphs={[
              "Geometry attributes are attached to each object so Rhino meshes stream into Cadwork with fabrication-ready metadata for timber and steel manufacturing.",
            ]}
          />

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure
                  src={`${BASE}/ForTimberAttributes.png`}
                  alt="Timber attribute schema — driver geometry"
                  ratio="classic"
                  fit="contain"
                  caption="Timber · driver schema"
                />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/AttributesTimber.PNG`}
                  alt="Timber fabrication attribute sets table"
                  ratio="classic"
                  fit="contain"
                  invert
                  caption="Timber · attribute table"
                />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure
                  src={`${BASE}/ForSteelAttributes.png`}
                  alt="Steel attribute schema — driver geometry"
                  ratio="classic"
                  fit="contain"
                  caption="Steel · driver schema"
                />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/AttributesSteel.PNG`}
                  alt="Steel fabrication attribute sets table"
                  ratio="classic"
                  fit="contain"
                  invert
                  caption="Steel · attribute table"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        {/* Assembled component outputs — 2 rows of 4+4+4 squares, then 6+6 of 4:3 */}
        <BlockSection title="Assembled component outputs">
          <RecipeBoard>
            <RecipeRow>
              {smallOutputs.slice(0, 3).map((name, idx, arr) => (
                <RecipeCell
                  key={name}
                  span={4}
                  className={`!p-0 ${idx === arr.length - 1 ? "!border-r-0" : ""}`}
                >
                  <BlockFigure
                    src={`${BASE}/${name}.png`}
                    alt={`SILT assembled component — ${name}`}
                    ratio="square"
                    fit="cover"
                  />
                </RecipeCell>
              ))}
            </RecipeRow>
            <RecipeRow>
              {smallOutputs.slice(3).map((name, idx, arr) => (
                <RecipeCell
                  key={name}
                  span={4}
                  className={`!p-0 ${idx === arr.length - 1 ? "!border-r-0" : ""}`}
                >
                  <BlockFigure
                    src={`${BASE}/${name}.png`}
                    alt={`SILT assembled component — ${name}`}
                    ratio="square"
                    fit="cover"
                  />
                </RecipeCell>
              ))}
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure
                  src={`${BASE}/Output8.png`}
                  alt="SILT assembly fabrication output 8"
                  ratio="classic"
                  fit="cover"
                />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/Output9.png`}
                  alt="SILT assembly fabrication output 9"
                  ratio="classic"
                  fit="cover"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>

        {/* Construction & built — 2 × (6 + 6), all classic 4:3 */}
        <BlockSection title="Construction & built work">
          <p className={blockItalicSecondaryNoteClass}>
            Photography credited to Studio ZJA.
          </p>

          <RecipeBoard>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure
                  src={`${BASE}/Construction1.PNG`}
                  alt="SILT Middelkerke — construction phase 1"
                  ratio="classic"
                  fit="cover"
                />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/Construction3.PNG`}
                  alt="SILT Middelkerke — construction phase 3"
                  ratio="classic"
                  fit="cover"
                />
              </RecipeCell>
            </RecipeRow>
            <RecipeRow>
              <RecipeCell span={6} className="!p-0">
                <BlockFigure
                  src={`${BASE}/Built1.PNG`}
                  alt="SILT Middelkerke — completed pavilion, view 1"
                  ratio="classic"
                  fit="cover"
                />
              </RecipeCell>
              <RecipeCell span={6} className="!p-0 !border-r-0">
                <BlockFigure
                  src={`${BASE}/Built2.PNG`}
                  alt="SILT Middelkerke — completed pavilion, view 2"
                  ratio="classic"
                  fit="cover"
                />
              </RecipeCell>
            </RecipeRow>
          </RecipeBoard>
        </BlockSection>
      </div>
    </main>
  );
}
